import { useEffect, useMemo, useRef, useState } from 'react';
import { matchesDesignSearch } from './search';
import { Check, Search, Upload, X } from 'lucide-react';
import { ScreenHeader } from '../../components/ScreenHeader';
import { ArtworkThumbnail } from '../../components/ArtworkThumbnail';
import { Button } from '../../components/Button';
import { StepProgress } from '../../components/StepProgress';
import { tattooDesigns, type DesignCategory, type TattooDesign } from '../../config/designs';
import { imageUploadErrorKey, MAX_UPLOAD_MEGABYTES, prepareImageFile } from '../../editor/image';
import { telegram } from '../../telegram/bridge';
import { useI18n } from '../../i18n/I18nProvider';
import { DEFAULT_DESIGN_OPACITY } from '../../../shared/catalog';
import { isDesignAssetCancellation } from '../../api/assets';

export interface CustomDesign {
  design: TattooDesign;
  objectUrl: string;
}

interface DesignLibraryProps {
  onSelect: (design: TattooDesign) => Promise<void>;
  onCustom: (custom: CustomDesign) => Promise<void>;
  onBack: () => void;
  selectedId?: string;
  compact?: boolean;
  designs?: TattooDesign[];
  hasMore?: boolean;
  loadingMore?: boolean;
  onLoadMore?: () => void;
}

export function DesignLibrary({ onSelect, onCustom, onBack, selectedId, compact = false, designs = tattooDesigns, hasMore, loadingMore, onLoadMore }: DesignLibraryProps) {
  const { t } = useI18n();
  const [category, setCategory] = useState<DesignCategory>('All');
  const [query, setQuery] = useState('');
  const [uploadError, setUploadError] = useState('');
  const [selectionError, setSelectionError] = useState('');
  const [failedSelection, setFailedSelection] = useState<{ design: TattooDesign; custom?: CustomDesign } | null>(null);
  const [selectingId, setSelectingId] = useState('');
  const upload = useRef<HTMLInputElement>(null);
  const customCount = useRef(0);
  const selectionOperation = useRef(0);
  const categories = useMemo(() => ['All', ...new Set(designs.flatMap((item) => item.categories?.length ? item.categories : [item.category]).filter(Boolean))], [designs]);
  const filtered = useMemo(() => designs.filter((design) => {
    const categoryMatch = category === 'All' || (design.categories?.length ? design.categories : [design.category]).includes(category);
    const queryMatch = matchesDesignSearch(design, query);
    return categoryMatch && queryMatch;
  }), [category, designs, query]);

  useEffect(() => () => { selectionOperation.current += 1; }, []);

  const attemptSelection = async (design: TattooDesign, custom?: CustomDesign): Promise<boolean> => {
    const operation = ++selectionOperation.current;
    setUploadError('');
    setSelectionError('');
    setFailedSelection(null);
    setSelectingId(design.id);
    try {
      if (custom) await onCustom(custom);
      else await onSelect(design);
      return operation === selectionOperation.current;
    } catch (cause) {
      const cancelled = isDesignAssetCancellation(cause) || (cause instanceof Error && cause.name === 'AbortError');
      if (operation === selectionOperation.current && !cancelled) {
        setFailedSelection({ design, ...(custom ? { custom } : {}) });
        setSelectionError(t('design.loadError'));
        telegram.haptic('error');
      }
      return false;
    } finally {
      if (operation === selectionOperation.current) setSelectingId('');
    }
  };

  const chooseCustom = async (file?: File) => {
    if (!file) return;
    setUploadError('');
    setSelectionError('');
    try {
      const prepared = await prepareImageFile(file, 1800, .92);
      const objectUrl = URL.createObjectURL(prepared);
      customCount.current += 1;
      const design: TattooDesign = {
        id: `custom-${Date.now()}`,
        name: t('design.customName', { count: customCount.current }),
        category: 'Illustrative',
        thumbnail: objectUrl,
        asset: objectUrl,
        defaultBlend: 'multiply',
        categories: ['Illustrative'],
        recommendedOpacity: DEFAULT_DESIGN_OPACITY,
      };
      const custom = { design, objectUrl };
      if (await attemptSelection(design, custom)) telegram.haptic('success');
    } catch (cause) {
      setUploadError(t(imageUploadErrorKey(cause), { size: MAX_UPLOAD_MEGABYTES }));
      telegram.haptic('error');
    } finally {
      if (upload.current) upload.current.value = '';
    }
  };

  const content = (
    <>
      {!compact && <ScreenHeader title={t('design.title')} onBack={onBack} />}
      <div className={compact ? 'design-library design-library--compact' : 'screen-content design-library'}>
        {!compact && <StepProgress current={0} />}
        <div className="section-heading"><div><p className="eyebrow">{t('design.eyebrow')}</p><h1>{t('design.heading')}</h1></div><p>{t('design.copy')}</p></div>
        <label className="search-field">
          <Search aria-hidden="true" />
          <span className="visually-hidden">{t('design.search')}</span>
          <input type="search" value={query} onChange={(event) => setQuery(event.target.value)} placeholder={t('design.searchPlaceholder')} />
          {query && <button type="button" className="search-field__clear" onClick={() => setQuery('')} aria-label={t('design.clear')}><X /></button>}
        </label>
        <div className="filter-chips" role="group" aria-label={t('design.categories')}>
          {categories.map((item) => <button key={item} className={category === item ? 'is-selected' : ''} aria-pressed={category === item} onClick={() => setCategory(item)}>{['All', 'Japanese', 'Illustrative', 'Lettering', 'Blackwork'].includes(item) ? t(`design.category.${item}` as 'design.category.All') : item}</button>)}
        </div>
        <button className="upload-design-card" onClick={() => upload.current?.click()}>
          <span><Upload aria-hidden="true" /></span><strong>{t('design.upload')}</strong><small>{t('design.formats', { size: MAX_UPLOAD_MEGABYTES })}</small>
        </button>
        <input ref={upload} className="visually-hidden" type="file" accept="image/jpeg,image/png,image/webp,image/heic,image/heif,.heic,.heif" onChange={(event) => void chooseCustom(event.target.files?.[0])} />
        {uploadError && <p className="inline-error" role="alert">{uploadError}</p>}
        {selectionError && failedSelection && <div className="design-load-error" role="alert">
          <p>{selectionError}</p>
          <Button variant="secondary" busy={selectingId === failedSelection.design.id} onClick={() => void attemptSelection(failedSelection.design, failedSelection.custom)}>{t('common.retry')}</Button>
        </div>}
        <div className="design-grid">
          {filtered.map((design) => (
            <button key={design.id} className={`design-card${selectedId === design.id ? ' is-selected' : ''}${selectingId === design.id ? ' is-loading' : ''}`} onClick={() => void attemptSelection(design)} aria-pressed={selectedId === design.id} aria-busy={selectingId === design.id}>
              <span className="design-card__image"><ArtworkThumbnail src={design.thumbnail} safeMargin={0} />{selectedId === design.id && <span className="design-card__check"><Check /></span>}{selectingId === design.id && <span className="design-card__loading" aria-hidden="true"><span className="spinner" /></span>}</span>
              <strong>{design.name}</strong><small>{['Japanese', 'Illustrative', 'Lettering', 'Blackwork'].includes(design.category) ? t(`design.category.${design.category}` as 'design.category.All') : design.category}</small>
            </button>
          ))}
        </div>
        {!filtered.length && <div className="empty-state"><p>{t('design.none')}</p><Button variant="secondary" onClick={() => { setQuery(''); setCategory('All'); }}>{t('design.clear')}</Button></div>}
        {hasMore && <Button block variant="secondary" busy={loadingMore} onClick={onLoadMore}>{t('design.more')}</Button>}
      </div>
    </>
  );

  return compact ? content : <main className="screen design-screen">{content}</main>;
}
